import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface CountUpProps {
    value: number
    decimals: number
    suffix: string
    epoch: number
    duration?: number
}

export const formatNumber = (value: number, decimals: number): string => {
    const fixed = value.toFixed(decimals)
    const [whole, fraction] = fixed.split('.')
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

    return fraction ? `${grouped}.${fraction}` : grouped
}

/**
 * A stat value that counts up from zero whenever `epoch` changes.
 */
export const CountUp: FC<CountUpProps> = ({ value, decimals, suffix, epoch, duration = 900 }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const reduced = useReducedMotion()
    const [initial] = useState(() => `${formatNumber(value, decimals)}${suffix}`)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        const final = `${formatNumber(value, decimals)}${suffix}`

        if (epoch === 0 || reduced) {
            element.textContent = final

            return
        }

        let raf = 0
        const start = performance.now()

        const frame = (now: number): void => {
            const progress = Math.min(1, (now - start) / duration)
            const eased = 1 - Math.pow(1 - progress, 3)

            element.textContent = progress < 1 ? `${formatNumber(value * eased, decimals)}${suffix}` : final

            if (progress < 1) raf = requestAnimationFrame(frame)
        }

        raf = requestAnimationFrame(frame)

        return () => {
            cancelAnimationFrame(raf)
            element.textContent = final
        }
    }, [value, decimals, suffix, epoch, duration, reduced])

    return (
        <span ref={ref} className='tabular-nums'>
            {initial}
        </span>
    )
}
